import { DeviceIdService } from "@service/device-id.service";

function getBaseApiUrl(): string {
  return process.env.REACT_APP_API_URL ?? "";
}

const MAX_REFRESH_ATTEMPTS = 3;

export class PatientTokenRefreshError extends Error {
  readonly kind: "transient" | "auth";

  constructor(message: string, kind: "transient" | "auth") {
    super(message);
    this.name = "PatientTokenRefreshError";
    this.kind = kind;
  }
}

function isTransientHttpStatus(status: number): boolean {
  return status >= 500 || status === 408 || status === 429;
}

export class TokenRefreshServiceService {
  public static instance = new TokenRefreshServiceService();
  private deviceIdService = DeviceIdService.instance;

  private constructor() {}

  async refreshToken(accessToken: string): Promise<{ accessToken: string }> {
    let lastError: unknown;
    for (let attempt = 0; attempt < MAX_REFRESH_ATTEMPTS; attempt++) {
      try {
        return await this.fetchRefreshOnce(accessToken);
      } catch (e) {
        lastError = e;
        if (
          e instanceof PatientTokenRefreshError &&
          e.kind === "transient" &&
          attempt < MAX_REFRESH_ATTEMPTS - 1
        ) {
          await new Promise((r) =>
            setTimeout(r, 150 * (attempt + 1)),
          );
          continue;
        }
        throw e;
      }
    }
    throw lastError instanceof Error
      ? lastError
      : new Error("Token refresh failed");
  }

  private async fetchRefreshOnce(
    accessToken: string,
  ): Promise<{ accessToken: string }> {
    const deviceId = this.deviceIdService.getDeviceId();

    let res: Response;
    try {
      res = await fetch(`${getBaseApiUrl()}/users/token/refresh?`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({
          deviceId,
          accessToken,
        }),
      });
    } catch {
      throw new PatientTokenRefreshError(
        "Network error during token refresh",
        "transient",
      );
    }

    if (!res.ok) {
      if (isTransientHttpStatus(res.status)) {
        throw new PatientTokenRefreshError(
          `Token refresh failed with status ${res.status}`,
          "transient",
        );
      }
      throw new PatientTokenRefreshError(
        `Token refresh failed with status ${res.status}`,
        "auth",
      );
    }

    let data: { accessToken?: string };
    try {
      data = (await res.json()) as { accessToken?: string };
    } catch {
      throw new PatientTokenRefreshError(
        "Invalid JSON in token refresh response",
        "auth",
      );
    }

    if (!data?.accessToken) {
      throw new PatientTokenRefreshError(
        "Token refresh response missing accessToken",
        "auth",
      );
    }

    return { accessToken: data.accessToken };
  }
}
